import { BlogPost } from './types';

export const examStrategyPosts: BlogPost[] = [
  {
    id: '7',
    slug: 'jee-mains-2026-surface-level-strategy',
    title: 'JEE Mains 2026: The "Surface Level" Strategy to Score 80+ in Physics',
    excerpt: 'With only two months left for the January attempt, discover the smart student roadmap to saving your Physics score. Learn why breadth beats depth in JEE Mains.',
    content: `
      <p>With only two months left for the January attempt, the panic is real. Every aspirant is asking the same question on Google and YouTube: <em>"Which chapters can I skip in Physics?"</em></p>

      <p>But here is the bitter truth: <strong>If you are looking for a list of chapters to skip, you are already preparing to fail.</strong></p>

      <p>The pattern of JEE Mains has changed. The paper is no longer about how <em>deep</em> you know a topic, but how <em>wide</em> your knowledge is. Here is the "Smart Student" roadmap to saving your Physics score in the final lap.</p>

      <h2>The "Paper is Easy" Trap</h2>
      <p>Recently, JEE Mains Physics papers have been surprisingly easy.</p>

      <ul>
        <li><strong>The Good News:</strong> 18 out of 25 questions are direct formula-based.</li>
        <li><strong>The Bad News:</strong> Because it is easy, the cutoff goes high.</li>
      </ul>

      <p>If the paper is tough, you can afford to leave questions. But when the paper is easy, making even one "silly mistake" or leaving a question because you didn't know a basic formula is a crime.</p>

      <img src="/img/blog/jee2026-1.png" alt="Student stressed over easy exam paper" class="img-fluid mb-3">     

      <h2>Strategy 1: The "Breadth Over Depth" Rule</h2>
      <p>Stop trying to become a PhD scholar in Mechanics right now. You don't need to solve complex Irodov-level problems for the January attempt.</p>

      <p><strong>The Golden Rule:</strong> You must touch <em>every</em> chapter, even the ones you hate.</p>

      <p>For example, many students hate <strong>Rotation</strong> or <strong>Fluids</strong>. They leave it completely. But in the exam, the question often asks for a simple "Moment of Inertia" or "Terminal Velocity." These are free marks! If you skip the chapter, you lose these easy +4 marks.</p>

      <p><strong>What to do instead:</strong><br>
      If a chapter is hard, just memorize the <strong>Formulas</strong> and <strong>Basic Concepts</strong>. Do not go deep, but do not leave it blank.</p>

      <img src="/img/blog/jee2026-4.png" alt="A brightly colored donut chart showing JEE Physics Weightage. The largest chunks are labeled 'Modern Physics', 'Optics', 'Electromagnetism', and 'Mechanics'." class="img-fluid mb-3">

      <h2>Strategy 2: The High-Yield Topics (The 50% Club)</h2>
      <p>If you analyze the recent trends (2024-2025), nearly 50% of the paper comes from specific heavy-weight units. These are non-negotiable:</p>

      <ol>
        <li><strong>Modern Physics:</strong> This is the MVP. Questions on De Broglie Wavelength and Photoelectric effect are guaranteed.</li>
        <li><strong>Optics (Ray & Wave):</strong> A big unit, but very scoring.</li>
        <li><strong>Electrostatics & Current Electricity:</strong> The backbone of Class 12th.</li>
        <li><strong>Thermodynamics:</strong> Easy to understand and highly repetitive questions.</li>
      </ol>

      <img src="/img/blog/jee2026-2.png" alt="A brightly colored donut chart showing JEE Physics Weightage. The largest chunks are labeled 'Modern Physics', 'Optics', 'Electromagnetism', and 'Mechanics'." class="img-fluid mb-3">
      
      <h2>Strategy 3: The "Laddoo" Marks</h2>
      <p>There are certain topics that take only 30 minutes to study but guarantee a question. If you skip these, you are hurting your own rank:</p>

      <ul>
        <li><strong>Logic Gates:</strong> It takes 20 minutes to learn AND, OR, NOT gates. One question is fixed.</li>
        <li><strong>EM Waves:</strong> Just learn the relation between E and B field (E₀ = cB₀).</li>
        <li><strong>Graphs:</strong> JEE Mains loves graphs. Check the V-T graphs in Kinematics and the characteristic graphs in Semiconductors (LED/Solar cells).</li>
      </ul>

      <img src="/img/blog/jee2026-3.png" alt="A close-up photo of a student's hand writing in a spiral notebook. The heading on the page says 'Formula Sheet - DO NOT FORGET'. The page is filled with neat, color-coded physics formulas. A cup of coffee is on the side." class="img-fluid mb-3">
      

      <h2>Your Action Plan for the Next 60 Days</h2>
      <p>Don't waste time watching motivational videos. Do these three things:</p>

      <ol>
        <li><strong>Create a Formula Diary:</strong> Buy a thin notebook. Write down <em>only</em> formulas for every single chapter. Revise this diary every morning.</li>
        <li><strong>Target Accuracy, Not Speed:</strong> Since the paper is easy, accuracy is the king. A calculation error is worse than not attempting a question.</li>
        <li><strong>Mock Tests:</strong> The only way to remove fear is to face it. Give full-syllabus mock tests to build your stamina.</li>
      </ol>      

      <h2>Final Verdict</h2>
      <p>The student who covers the full syllabus at a basic level will beat the student who masters only 50% of the syllabus at an advanced level. <strong>Study smart!</strong></p>

      <div class="alert alert-info mt-4">
        <i class="fas fa-lightbulb me-2"></i>
        <strong>Pro Tip:</strong> Use WiZiAi's personalized practice tests to identify your weak topics and focus on breadth-based preparation. Our AI adapts to your learning style and ensures you cover all chapters effectively!
      </div>
    `,
    author: {
      name: 'Amit Sharma',
      role: 'JEE Physics Expert & IIT Alumni'
    },
    category: 'Exam Strategy',
    tags: ['JEE Mains 2026', 'Physics', 'Study Strategy', 'Exam Preparation', 'Last Minute Tips'],
    featuredImage: '/img/blog/jee2026-cover.png',
    publishedDate: '2025-12-04',
    readTime: '7 min read',
    views: 450
  }
];
